import React, { useEffect, useState, useRef, useCallback } from 'react';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import InfoIcon from "../support_files/InfoIcon";
import HelpIcon from "../support_files/HelpIcon";
import ToggleButton from '@mui/material/ToggleButton';

import PublicIcon from '@mui/icons-material/Public';
import CancelIcon from '@mui/icons-material/Cancel';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import TocIcon from '@mui/icons-material/Toc';
import SourceIcon from '@mui/icons-material/Source';

import Fade from '@mui/material/Fade';

import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTheme } from '@mui/material/styles';

import Box from '@mui/material/Box';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';

import { ResponsiveBar } from '@nivo/bar'

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import ancestryInfoData from "../info_data/ancestryInfoData.json"

import { createTheme, ThemeProvider } from '@mui/material/styles';

import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

export default function AncestryInfoDrawer({
  selectedInfo, setSelectedInfo,
}) {

  const racialGroups = ['Asian', 'Black or African American', 'Latin American', 'Middle Eastern or North African', 'Native American', 'Pacific Islander', 'White'];

  // Use ref to directly manipulate DOM classes - no re-renders
  const headerRef = useRef(null);
  const handleScroll = useCallback((event) => {
    const scrollTop = event.target.scrollTop;
    if (headerRef.current) {
      if (scrollTop > 20) {
        headerRef.current.classList.add('header-translucent');
        headerRef.current.classList.remove('header-solid');
      } else {
        headerRef.current.classList.add('header-solid');
        headerRef.current.classList.remove('header-translucent');
      }
    }
  }, []); // Empty dependency array since we're using refs

  const AncestryBarChart = ({ racialGroup }) => {
      // Helper function to truncate long ancestry names
 const truncateAncestry = (ancestryName) => {
    if (ancestryName.length > 18) {
      return ancestryName.substring(0, 15) + '...';
    }
    return ancestryName;
  };

  // Filter data by racial group, sort by percentage, then take top 15
  const groupData = ancestryInfoData
    .filter(item => item.racial_group === racialGroup)
    .sort((a, b) => parseFloat(b.decimal) - parseFloat(a.decimal)) // Sort by percentage descending
    .slice(0, 15) // Show top 15 for readability
    .map(item => ({
      ancestry: truncateAncestry(item.ancestry), // Truncate here
      originalAncestry: item.ancestry, // Keep original for tooltip
      percentage: parseFloat(item.decimal) * 100,
      count: item.count,
      percent: item.percent,
      ancestry_group: item.ancestry_group
    }));

  // Color mapping for racial groups
  const getColor = (racialGroup) => {
    const colorMap = {
      'Asian': '#ca7e8cff',
      'Black or African American': '#a6b987ff',
      'Latin American': '#e69e60ff',
      'Middle Eastern or North African': '#62bb7eff',
      'Native American': '#ceba4bff',
      'Pacific Islander': '#6cbdaeff',
      'White': '#72a4c3ff',
      'Other': '#c5b4e3',
    };
    return colorMap[racialGroup] || '#888888';
  };
  
    return (
      <div style={{ height: 400, width: '100%' }}>
        <ResponsiveBar
          data={groupData}
          keys={['percentage']}
          indexBy="ancestry"
          layout="horizontal"
          margin={{ top: 10, right: 15, bottom: 60, left: 100 }}
          padding={0.3}
          valueScale={{ type: 'linear', min: 0, max: 'auto' }}
          indexScale={{ type: 'band', round: true }}
          colors={() => getColor(racialGroup)}
          borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Percentage',
            legendPosition: 'middle',
            legendOffset: 36,
            format: value => `${value.toFixed(1)}%`,
            tickValues: 5
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Ancestry',
            legendPosition: 'middle',
            legendOffset: -95,
          }}
          enableLabel={true}
          label={({ value }) => `${value.toFixed(1)}%`}
              labelSkipWidth={4}
          labelSkipHeight={12}
          labelTextColor="#ffffff"
          labelOffset={10}        // Push labels away from bars (positive = right, negative = left)
labelTextAnchor="start" // Options: 'start', 'middle', 'end' 
          tooltip={({ indexValue, value, color, data }) => (
            <div
              style={{
                background: 'rgba(0, 0, 0, 0.8)',
                color: '#ffffff',
                fontSize: '14px',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                padding: '8px 12px'
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                {data.originalAncestry}
              </div>
              {/* <div style={{ fontSize: '12px', opacity: 0.8 }}>
                {data.ancestry_group}
              </div> */}
              <div style={{ fontWeight: '500', marginBottom: '2px' }}>
                {data.percent}
              </div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>
                {data.count} people
              </div>
            </div>
          )}
          theme={{
            background: 'transparent',
            text: {
              fill: '#ffffff',
              fontSize: 11
            },
            axis: {
              domain: {
                line: {
                  stroke: '#777777',
                  strokeWidth: 1
                }
              },
              legend: {
                text: {
                  fill: '#ffffff',
                  fontSize: 12
                }
              },
              ticks: {
                line: {
                  stroke: '#777777',
                  strokeWidth: 1
                },
                text: {
                  fill: '#ffffff',
                  fontSize: 11
                }
              }
            },
            grid: {
              line: {
                stroke: '#444444',
                strokeWidth: 1
              }
            }
          }}
          animate={true}
          motionConfig="gentle"
        />
      </div>
    );
  };

  const AncestryTable = ({ racialGroup }) => {
        const handleTouchStart = (e) => {
      e.stopPropagation();
    };

    const handleTouchMove = (e) => {
      e.stopPropagation();
    };

    // Show ALL ancestry values for each racial group, sorted alphabetically by ancestry group, then ancestry
    const groupData = ancestryInfoData
      .filter(item => item.racial_group === racialGroup)
      .sort((a, b) => {
        // First sort by ancestry_group alphabetically
        if (a.ancestry_group < b.ancestry_group) return -1;
        if (a.ancestry_group > b.ancestry_group) return 1;
        // If ancestry_group is the same, sort by ancestry alphabetically
        if (a.ancestry < b.ancestry) return -1;
        if (a.ancestry > b.ancestry) return 1;
        return 0;
      });

    return (
      <TableContainer
        component={Paper}
                onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        sx={{
          overflow: 'auto',
          marginBottom: '20px',
          borderRadius: '12px',
          backgroundColor: '#1a1a1a',
          '& .MuiTable-root': {
            '& .MuiTableCell-root': {
              color: 'white',
              borderBottom: '1px solid #444'
            },
            '& .MuiTableRow-root:hover': {
              backgroundColor: '#333'
            }
          }
        }}
      >
        <Table sx={{ maxWidth: 800 }} size="small" aria-label="ancestry table">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  backgroundColor: '#2d2d2d',
                  zIndex: 1,
                  boxShadow: '2px 0 5px rgba(0,0,0,0.1)'
                }}
              >
                Ancestry
              </TableCell>
              <TableCell align="right" sx={{ width: '200px' }}>
                Ancestry Group
              </TableCell>
              <TableCell align="right" sx={{ width: '100px' }}>
                Percentage
              </TableCell>
                            <TableCell align="right" sx={{ width: '100px' }}>
                Count
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupData.map((item, index) => (
              <TableRow
                key={`${item.ancestry_group}-${item.ancestry}-${index}`}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    position: 'sticky',
                    left: 0,
                    backgroundColor: '#2d2d2d',
                    zIndex: 1,
                    boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
                    fontWeight: 500,
                    padding: '6px 10px'
                  }}
                >
                  {item.ancestry}
                </TableCell>
                <TableCell align="right">
                  {item.ancestry_group}
                </TableCell>
                <TableCell align="right">
                  {item.percent}
                </TableCell>
                <TableCell align="right">
                  {item.count}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const drawerContent = () => (
    <div className="drawer-container" style={{ overflow: 'auto' }} onScroll={handleScroll}>
      <div ref={headerRef} className="sticky-header header-solid">
        <div className="header-content">
          <div className="header-left">
            <PublicIcon />
            <h2 className="header-title">Ancestry</h2>
          </div>
          <div className="header-right">
            <button
              onClick={() => { setSelectedInfo(false) }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <CancelIcon sx={{ fill: "#b4b4b4ff", fontSize: "28px" }} />
            </button>
          </div>
        </div>
      </div>

      <div className="main-content">
        <div className="section">
          <div className="card">
            <div className="section-header">
              <div className="icon-holder">
                <InsertChartIcon sx={{ fontSize: "18px" }} />
              </div>
              <h3 className="section-title">Summary</h3>
            </div>
            <div style={{ height: '440px' }}>
              <Swiper
                pagination={true}
                navigation={true}
                className="mySwiper"
                touchStartPreventDefault={false}
                preventClicks={false}
                preventClicksPropagation={false}
                allowSlidePrev={true}
                allowSlideNext={true}
                simulateTouch={true}
                touchMoveStopPropagation={false}
                modules={[Pagination, Navigation]}
                style={{
                  '--swiper-navigation-color': 'rgba(255, 255, 255, 0.2)',
                  '--swiper-navigation-size': '22px',
                  height: '440px'
                }}
              >
                {racialGroups.map(group => (
                  <SwiperSlide key={group}>
                    <div className="section-header" style={{ marginBottom: "10px", justifyContent: "center" }}>
                      <h3 className="section-title" style={{ fontWeight: 600 }}>
                        {group}
                      </h3>
                    </div>
                    <AncestryBarChart racialGroup={group} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
            <p className="card-text">
              These charts illustrate the top 15 ancestries within each major racial group in New York City as of 2020 according to the US Census¹. The percentages represent each ancestry’s share of the total NYC population.
            </p>
          </div>
        </div>

        <div className="section">
          <div className="card">
            <div className="section-header">
              <div className="icon-holder">
                <TocIcon sx={{ fontSize: "18px" }} />
              </div>
              <h3 className="section-title">Details</h3>
            </div>
            <div className="visual-container">
              <div style={{ height: '420px' }}>
                <Swiper
                  pagination={true}
                  navigation={true}
                  className="mySwiper"
                  touchStartPreventDefault={false}
                  preventClicks={false}
                  preventClicksPropagation={false}
                  allowSlidePrev={true}
                  allowSlideNext={true}
                  simulateTouch={true}
                  touchMoveStopPropagation={false}
                  modules={[Pagination, Navigation]}
                  style={{
                    '--swiper-navigation-color': 'rgba(255, 255, 255, 0.2)',
                    '--swiper-navigation-size': '22px',
                    height: '430px'
                  }}
                >
                  {racialGroups.map(group => (
                    <SwiperSlide key={group}>
                      <div className="section-header" style={{ marginBottom: "20px", justifyContent: "center" }}>
                        <h3 className="section-title" style={{ fontWeight: 600 }}>
                          {group}
                        </h3>
                      </div>
                      <div style={{ height: '340px', overflow: 'scroll', paddingBottom: "20px" }}>
                        <AncestryTable racialGroup={group} />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
          </div>
        </div>

        <div className="section">
          <div className="card">
            <div className="section-header">
              <div className="icon-holder">
                <SourceIcon sx={{ fontSize: "18px" }} />
              </div>
              <h3 className="section-title">Source</h3>
            </div>
            <p className="card-text">
              [1] U.S. Census Bureau, 2020 American Community Survey (NYC)
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <SwipeableDrawer
        anchor="bottom"
        open={selectedInfo}
        onClose={() => setSelectedInfo(false)}
        onOpen={() => setSelectedInfo(true)}
        sx={{
          '& .MuiDrawer-paper': {
            backgroundColor: '#071c34ff',
            color: "white",
            borderTopRightRadius: '25px 15px',
            borderTopLeftRadius: '25px 15px',
            height: 'calc(100dvh - 95px)',
            overflow: 'auto'
          },
        }}
      >
        <div className="additionalInfoContainer">
          {drawerContent()}
        </div>
      </SwipeableDrawer>
    </div>
  )
}